import type { ConstructionSubMode } from "../tools/constructionSubModes";

/**
 * The construction sub-tools: order, titles, keyboard shortcuts. The buttons
 * themselves are static markup in index.html (#1230), so the page shows the
 * default tool and sub-tool before the app's JavaScript runs;
 * scripts/constructionSubbarHtml.test.ts keeps that markup in step with this
 * list.
 */
export const SUBTOOL_SHORTCUTS: Record<ConstructionSubMode, string> = {
  draw: "d", // mnemonic: Draw
  select: "v", // mnemonic: V — same letter the leather Select tool
  //                uses for "default pointer / pick" (issue #330).
  //                The construction-gated branch fires before the
  //                global leather-Select branch when Construction
  //                is active, so the two share the letter without
  //                clashing.
  edit: "a", // mnemonic: Anchor (issue #331). Sub-mode label is
  //                "Edit anchor points"; "A" reads more
  //                naturally than "E" for the gesture.
  // The plain mnemonic keys drive the SIMPLE (default) variants;
  // the live "modifier" versions have no single-key shortcut (#344).
  "simple-offset": "o", // mnemonic: Offset
  offset: "",
  "simple-mirror": "i", // mnemonic: mIrror
  mirror: "",
  round: "r", // mnemonic: Round
  delete: "x", // mnemonic: X-out / delete
};
export const SUBTOOL_TITLES: Record<ConstructionSubMode, string> = {
  draw: "Draw construction lines",
  select: "Select / move construction lines",
  edit: "Edit anchor points",
  "simple-offset": "Offset a construction line (standalone copy)",
  offset: "Offset modifier — derived line follows its source",
  "simple-mirror": "Mirror selected lines across an axis (standalone copy)",
  mirror: "Mirror modifier — reflected line follows its source",
  round: "Round corners",
  delete:
    "Delete — tap a segment, Shift-tap a whole line, or swipe across several",
};
// Order matters — Draw leads since it's the default mode the user
// lands in when activating the Construction tool. Edit sits last
// (after Round) so the sub-sub-tool cluster appended below the
// main row reads as nested under Edit, and the most-touched edit
// affordances stay adjacent to their parent button (issue #269).
// Mirror sits next to Offset so the two "derive a line from another"
// sub-tools cluster together.
export const SUBTOOL_ORDER: ConstructionSubMode[] = [
  "draw",
  "select",
  // Each "simple" default sits next to its wand-badged "modifier"
  // twin (issue #344) so the pair reads together.
  "simple-offset",
  "offset",
  "simple-mirror",
  "mirror",
  "round",
  "edit",
  // Delete sits right after Edit — it shares Edit's handle targeting
  // but is its own top-level sub-tool (issue #670 follow-up).
  "delete",
];

/** A sub-tool's tooltip and accessible name: its title plus its shortcut. */
export function subtoolLabel(id: ConstructionSubMode): string {
  const shortcut = SUBTOOL_SHORTCUTS[id];
  return shortcut
    ? `${SUBTOOL_TITLES[id]} (${shortcut.toUpperCase()})`
    : SUBTOOL_TITLES[id];
}
